/**
 * Vertical Configuration Hook
 *
 * Provides access to vertical-specific labels, features, and metadata schemas
 * for the current tenant. Falls back to neutral defaults when no config is available.
 */

import { useAuthStore } from '../store'

export interface VerticalLabels {
  entity: string
  payer: string
  entity_reference: string
  group: string
  obligation: string
  period: string
  institution: string
  institution_detail: string
}

export interface VerticalFeatures {
  academics?: boolean
  fee_structures?: boolean
  announcements?: boolean
  family_grouping?: boolean
  boarding?: boolean
  streams?: boolean
  property_management?: boolean
  leases?: boolean
}

export interface MetadataFieldSchema {
  type: string
  label: string
  options?: string[]
}

export interface EntityMetadataSchema {
  [key: string]: MetadataFieldSchema
}

export interface ImportColumnDef {
  key: string
  label: string
  required: boolean
  formDataKey: string
}

export interface AccountFormatOption {
  value: string
  label: string
}

export interface VerticalConfig {
  labels: VerticalLabels
  features: VerticalFeatures
  obligationCategories: string[]
  entityTypes: string[]
  entityMetadataSchema: EntityMetadataSchema
  periodType: string
  payerIsBeneficiary: boolean
  paymentMatchingPattern: string
  importColumns: ImportColumnDef[]
  accountFormatOptions: AccountFormatOption[]
}

// Neutral defaults used when tenant vertical config is unavailable.
const defaultLabels: VerticalLabels = {
  entity: 'Entity',
  payer: 'Payer',
  entity_reference: 'Reference',
  group: 'Group',
  obligation: 'Obligation',
  period: 'Billing Period',
  institution: 'Institution',
  institution_detail: 'Institution Details',
}

const defaultFeatures: VerticalFeatures = {}

const defaultObligationCategories = ['other']

const defaultEntityTypes = ['default']

const defaultEntityMetadataSchema: EntityMetadataSchema = {}

const defaultImportColumns: ImportColumnDef[] = [
  { key: 'entity_reference', label: 'Reference', required: true, formDataKey: 'col_entity_reference' },
  { key: 'entity_name', label: 'Entity Name', required: true, formDataKey: 'col_entity_name' },
  { key: 'payer_phone', label: 'Payer Phone', required: true, formDataKey: 'col_payer_phone' },
  { key: 'amount', label: 'Amount', required: true, formDataKey: 'col_balance' },
  { key: 'payer_name', label: 'Payer Name', required: false, formDataKey: 'col_payer_name' },
  { key: 'entity_segment', label: 'Segment', required: false, formDataKey: 'col_grade' },
]

const defaultAccountFormatOptions: AccountFormatOption[] = [
  { value: '{entity_reference}', label: 'Reference Only' },
  { value: 'custom', label: 'Custom (set later)' },
]

/**
 * Hook to access vertical-specific configuration
 *
 * Returns labels, features, and metadata schemas based on the tenant's vertical.
 * Falls back to neutral defaults when no vertical config is available.
 */
export function useVerticalConfig(): VerticalConfig {
  const { tenant } = useAuthStore()

  // Get vertical config from tenant (populated from backend)
  const verticalConfig = tenant?.vertical_config

  return {
    labels: { ...defaultLabels, ...verticalConfig?.labels },
    features: verticalConfig?.features ?? defaultFeatures,
    obligationCategories:
      verticalConfig?.obligation_categories ?? defaultObligationCategories,
    entityTypes: verticalConfig?.entity_types ?? defaultEntityTypes,
    entityMetadataSchema:
      verticalConfig?.entity_metadata_schema ?? defaultEntityMetadataSchema,
    periodType: verticalConfig?.period_type ?? 'term',
    payerIsBeneficiary: verticalConfig?.payer_is_beneficiary ?? false,
    paymentMatchingPattern:
      verticalConfig?.payment_matching_pattern ?? '{entity_reference}',
    importColumns: verticalConfig?.import_columns ?? defaultImportColumns,
    accountFormatOptions:
      verticalConfig?.account_format_options ?? defaultAccountFormatOptions,
  }
}

/**
 * Check if a specific feature is enabled for the current vertical
 */
export function useFeatureEnabled(feature: keyof VerticalFeatures): boolean {
  const { features } = useVerticalConfig()
  return features[feature] ?? false
}

/**
 * Get the display label for a vertical concept
 */
export function useVerticalLabel(
  key: keyof VerticalLabels,
  plural = false
): string {
  const { labels } = useVerticalConfig()
  const label = labels[key]

  if (plural) {
    // Simple pluralization
    if (label.endsWith('y')) {
      return label.slice(0, -1) + 'ies'
    }
    if (label.endsWith('s') || label.endsWith('x') || label.endsWith('ch')) {
      return label + 'es'
    }
    return label + 's'
  }

  return label
}

/**
 * Get import column definitions from vertical config
 */
export function useImportColumns(): ImportColumnDef[] {
  const { importColumns } = useVerticalConfig()
  return importColumns
}
